/**
 * LE CHIFFRE QUE LA FINALISATION ARRÊTE — SANS NAVIGATEUR (11/10/2026).
 *
 * « Juste avant de finaliser la clôture, ça affiche le total sans Shotgun,
 * mais il faut le total » (l'exploitant). Le pied de l'assistant et la
 * confirmation montraient le RETENU des lignes : le sur-place seul, offerts
 * compris. Depuis le 11/10/2026, la finalisation arrête le chiffre officiel
 * de la nuit : le sur-place (hors offerts) + ce que Shotgun reverse pour les
 * préventes en ligne. Le 01/10 : 17 122,15 € + 453,00 € = 17 575,15 €.
 *
 * Les chiffres viennent du SERVEUR (`totals.onsite_cents`, `presales`) — la
 * même fonction que celle qui écrit les composantes : ce que l'écran annonce
 * est ce qui sera arrêté, au centime. Un reversé inconnu n'est jamais 0, un
 * reversé caché par les droits non plus : le total se tait, et la phrase dit
 * pourquoi. Un serveur d'avant ce contrat ne sert rien : `servi: false`, et
 * l'écran retombe sur ce qu'il disait avant.
 *
 * Ce fichier est pur : les tests le nourrissent d'une clôture et lisent ses
 * phrases.
 */

import { nuitLongue, type ClosingDetail } from './ClosingCommon.js'
import { formatMoney } from './money.js'

export interface ChiffreOfficiel {
  /** Le serveur sert le sur-place et les préventes (contrat du 11/10/2026). */
  servi: boolean
  /** Sur place, hors offerts (ce que la finalisation écrit). */
  surPlace: number | null
  /** Préventes reversées par Shotgun ; `null` = inconnues ou masquées. */
  preventes: number | null
  /** Connues du serveur, cachées par les droits de l'acteur. */
  masquees: boolean
  /** Sur place + préventes ; `null` dès qu'une part manque. */
  total: number | null
  /** « d’après la reddition R-… » / « d’après 108 billets en ligne » ; `null` sans préventes ou sans droit. */
  provenance: string | null
  /** Figées par la finalisation. */
  figees: boolean
  /**
   * La billetterie des associations (Pretix) qui entre au chiffre, parts
   * déduites (16/10/2026) ; `null` = rien (réglage éteint, aucun événement)
   * ou montant réservé (`associationsMasquees`).
   */
  associations: number | null
  /** Elle entre au chiffre, mais les droits de l'acteur en cachent le montant. */
  associationsMasquees: boolean
}

const nombre = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

/** Un montant dans une phrase : jamais coupé avant « € ». */
const m = (c: number): string => formatMoney(c).replace(/ €$/, ' €')

export function chiffreOfficiel(d: ClosingDetail): ChiffreOfficiel {
  const p = d.presales ?? null
  const surPlace = nombre(d.totals.onsite_cents)
  if (p === null || surPlace === null) {
    return { servi: false, surPlace: null, preventes: null, masquees: false, total: null, provenance: null, figees: false, associations: null, associationsMasquees: false }
  }
  const preventes = nombre(p.payout_cents)
  const masquees = preventes === null && p.masked === true
  let provenance: string | null = null
  if (preventes !== null && preventes !== 0) {
    const refs = p.reddition_refs.length > 0 ? ` ${p.reddition_refs.join(', ')}` : ''
    const billets = `${p.tickets} billet${p.tickets > 1 ? 's' : ''} en ligne`
    provenance = p.source === 'REDDITION' ? `d’après la reddition${refs}`
      : p.source === 'BILLETS' ? (p.tickets > 0 ? `d’après ${billets}` : 'd’après les billets en ligne')
        : p.source === 'REDDITION_ET_BILLETS' ? `d’après la reddition${refs} et les billets en ligne`
          : null
  }
  // La billetterie des associations : ajoutée quand le serveur la sert au
  // chiffre ; réservée par les droits, elle emporte le total (jamais une somme partielle).
  const a = d.associations ?? null
  const associations = a === null ? null : nombre(a.revenue_cents)
  const associationsMasquees = a !== null && a.revenue_enabled && associations === null && a.masked === true
  return {
    servi: true, surPlace, preventes, masquees,
    total: preventes === null || associationsMasquees ? null : surPlace + preventes + (associations ?? 0),
    provenance, figees: p.frozen, associations, associationsMasquees,
  }
}

/**
 * La ventilation, en une ligne : « dont 17 122,15 € sur place · 453,00 € de
 * préventes reversées par Shotgun ». `null` quand le serveur ne la sert pas.
 */
export function ventilationDuChiffre(c: ChiffreOfficiel): string | null {
  if (!c.servi || c.surPlace === null) return null
  if (c.preventes === 0) return `${m(c.surPlace)} sur place, aucune prévente en ligne`
  const enLigne = c.preventes !== null
    ? `${m(c.preventes)} de préventes reversées par Shotgun`
    : c.masquees
      ? 'préventes reversées par Shotgun (montant réservé : vos droits ne le montrent pas)'
      : 'préventes Shotgun : montant pas encore connu'
  const asso = typeof c.associations === 'number' && c.associations !== 0
    ? ` · ${m(c.associations)} de billetterie des associations, parts déduites`
    : c.associationsMasquees === true ? ' · billetterie des associations (montant réservé)' : ''
  return `dont ${m(c.surPlace)} sur place · ${enLigne}${asso}`
}

/**
 * Le message de la confirmation de finalisation, et ses lignes de détail.
 * Le total quand il est connu ; sinon le sur-place, et ce qui manque.
 */
export function confirmationDuChiffre(c: ChiffreOfficiel, retenuDesLignes: number): {
  message: string
  details: Array<{ label: string; value: string }>
} {
  const fin = 'Après la finalisation, plus rien ne se modifie — corriger demandera de créer une révision.'
  if (!c.servi || c.surPlace === null) {
    return { message: `Le retenu de cette nuit deviendra le chiffre officiel : ${formatMoney(retenuDesLignes)}. ${fin}`, details: [] }
  }
  const details: Array<{ label: string; value: string }> = [
    { label: 'Sur place, hors offerts', value: formatMoney(c.surPlace) },
  ]
  if (c.preventes === 0) {
    details.push({ label: 'Préventes reversées par Shotgun', value: 'aucune' })
  } else {
    details.push({
      label: c.provenance ? `Préventes reversées par Shotgun, ${c.provenance}` : 'Préventes reversées par Shotgun',
      value: c.preventes !== null ? formatMoney(c.preventes) : c.masquees ? 'montant réservé' : 'pas encore connues',
    })
  }
  const avecAssociations = typeof c.associations === 'number' && c.associations !== 0
  if (avecAssociations || c.associationsMasquees === true) {
    details.push({
      label: 'Billetterie des associations (Pretix), parts déduites',
      value: avecAssociations ? formatMoney(c.associations!) : 'montant réservé',
    })
  }
  if (c.total !== null) details.push({ label: 'Chiffre officiel de la nuit', value: formatMoney(c.total) })
  const message = c.total !== null && avecAssociations
    ? `Le chiffre officiel de la nuit sera de ${m(c.total)} : ${m(c.surPlace)} encaissés sur place, ${c.preventes === 0 ? 'aucune prévente en ligne' : `${m(c.preventes!)} de préventes reversées par Shotgun`} et ${m(c.associations!)} de billetterie des associations, parts déduites. ${fin}`
    : c.total !== null
    ? c.preventes === 0
      ? `Le chiffre officiel de la nuit sera de ${m(c.total)}, encaissés sur place : aucune prévente en ligne. ${fin}`
      : `Le chiffre officiel de la nuit sera de ${m(c.total)} : ${m(c.surPlace)} encaissés sur place et ${m(c.preventes!)} de préventes reversées par Shotgun. ${fin}`
    : `Le chiffre officiel de la nuit comptera ${m(c.surPlace)} encaissés sur place et les préventes reversées par Shotgun${c.masquees ? ', dont le montant est réservé : vos droits ne le montrent pas' : ''}. ${fin}`
  return { message, details }
}

/**
 * LE RÉCAPITULATIF DE LA FINALISATION — une seule rédaction pour la
 * confirmation simple et pour la fenêtre motivée (16/10/2026). La fenêtre
 * motivée, celle des nuits qui baissent, n'en disait plus rien : le geste le
 * plus lourd montrait MOINS que l'ordinaire (ventilation, points de vente
 * retenus, écarts encore ouverts — qui ne bloquent pas la finalisation).
 */
export function recapFinalisation(d: ClosingDetail): {
  message: string
  details: Array<{ label: string; value: string }>
} {
  const ouverts = d.pos.filter((p) => p.was_open === true)
  const ecartsOuverts = d.discrepancies.filter((e) => e.status === 'OUVERT')
  const { message, details } = confirmationDuChiffre(chiffreOfficiel(d), Number(d.totals.retained_cents ?? 0))
  return {
    message,
    details: [
      { label: 'Nuit', value: nuitLongue(d.business_date) },
      ...details,
      { label: 'Points de vente retenus', value: `${ouverts.length}` },
      { label: 'Écarts encore ouverts', value: ecartsOuverts.length === 0 ? 'aucun' : `${ecartsOuverts.length}` },
    ],
  }
}
